import { useEffect, useRef, useCallback } from 'react';
import { Renderer, Stave, StaveNote, Voice, Formatter, Accidental } from 'vexflow';
import type { Note } from '../types/music';
import { getVexFlowNoteName, PITCH_CLASS_COLORS } from '../utils/noteMappings';

interface StaffNoteProps {
  /** The note to render */
  note: Note;
  /** Width of the rendering area */
  width?: number;
  /** Height of the rendering area */
  height?: number;
  /** Whether to color-code the note by pitch class */
  colorCode?: boolean;
  /** Optional className for the container */
  className?: string;
}

/**
 * Renders a musical note on a staff using VexFlow
 *
 * Supports both treble and bass clef, accidentals, and ledger lines.
 * Notes are automatically positioned correctly based on their pitch and octave.
 */
export function StaffNote({
  note,
  width = 200,
  height = 150,
  colorCode = false,
  className = ''
}: StaffNoteProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<Renderer | null>(null);

  const renderNote = useCallback(() => {
    if (!containerRef.current) return;

    // Clear previous content
    containerRef.current.innerHTML = '';

    // Create renderer
    const renderer = new Renderer(containerRef.current, Renderer.Backends.SVG);
    rendererRef.current = renderer;

    // Configure the rendering context
    renderer.resize(width, height);
    const context = renderer.getContext();
    context.setFont('Arial', 10);

    // Calculate stave position for centering
    const staveWidth = width - 40;
    const staveX = 20;
    const staveY = 25;

    // Create the stave with clef
    const stave = new Stave(staveX, staveY, staveWidth);
    stave.addClef(note.clef);
    stave.setContext(context).draw();

    // Get VexFlow note name (e.g., "c/4" for C4)
    const vexNoteName = getVexFlowNoteName(note);

    // Determine note color
    const noteColor = colorCode ? PITCH_CLASS_COLORS[note.pitchClass] : '#000000';

    // Create the note
    const staveNote = new StaveNote({
      clef: note.clef,
      keys: [vexNoteName],
      duration: 'q', // Quarter note
    });

    // Add accidental if needed
    if (note.accidental === 'sharp') {
      staveNote.addModifier(new Accidental('#'), 0);
    } else if (note.accidental === 'flat') {
      staveNote.addModifier(new Accidental('b'), 0);
    }

    // Apply color styling
    staveNote.setStyle({ fillStyle: noteColor, strokeStyle: noteColor });

    // Create a voice and add the note
    const voice = new Voice({ numBeats: 1, beatValue: 4 });
    voice.addTickables([staveNote]);

    // Format and draw
    new Formatter().joinVoices([voice]).format([voice], staveWidth - 60);
    voice.draw(context, stave);
  }, [note, width, height, colorCode]);

  // Render on mount and when note changes
  useEffect(() => {
    renderNote();
  }, [renderNote]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      renderNote();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [renderNote]);

  return (
    <div
      ref={containerRef}
      className={`staff-note-container ${className}`}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      aria-label={`Musical note on ${note.clef} clef`}
      role="img"
    />
  );
}

/**
 * Renders an empty staff (no note) for loading states
 */
export function EmptyStaff({
  clef = 'treble',
  width = 200,
  height = 150,
  className = ''
}: {
  clef?: 'treble' | 'bass';
  width?: number;
  height?: number;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    containerRef.current.innerHTML = '';

    const renderer = new Renderer(containerRef.current, Renderer.Backends.SVG);
    renderer.resize(width, height);
    const context = renderer.getContext();

    const staveWidth = width - 40;
    const staveX = 20;
    const staveY = 25;

    const stave = new Stave(staveX, staveY, staveWidth);
    stave.addClef(clef);
    stave.setContext(context).draw();
  }, [clef, width, height]);

  return (
    <div
      ref={containerRef}
      className={`empty-staff-container ${className}`}
      style={{
        width: `${width}px`,
        height: `${height}px`
      }}
      aria-label={`Empty ${clef} clef staff`}
      role="img"
    />
  );
}
